import { memo, useState, useCallback, useRef, useEffect } from "react";
import {
  splitThinkingContent,
  filterToolOutputBlocks,
} from "@/shared/lib/utils";
import { EnrichedBlock } from "./enriched-block";
import type { ToolCall } from "@/hooks/use-send-message"; // Import ToolCall type
import Icon from "@/components/ui/icon";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useExecutedToolsSnippets } from "@/shared/hooks/use-executed-tools-snippets";
// Fix scrollbar styling for the entire component
import "./scrollbar-fix.css";
import Image from "next/image";

export interface ResponseBlockProps {
  response: string;
  reasoning?: string;
  tool_calls?: ToolCall[];
  // Callback to notify parent about visibility toggle
  onToggleToolVisibility?: (toolCallIndexValue: number) => void;
}

const ToolCallBlock = memo(
  ({
    toolCall,
    onToggleVisibility,
  }: {
    toolCall: ToolCall;
    onToggleVisibility?: (toolCallIndexValue: number) => void;
  }) => {
    const handleToggle = useCallback(() => {
      onToggleVisibility?.(toolCall.index);
    }, [onToggleVisibility, toolCall.index]);

    // Check if the tool is currently being called (has no output yet)
    const isLoading = toolCall.output === undefined || toolCall.output === null;

    // Extract function arguments from format like: search(query text) or python(code)
    let functionArgs = "";
    if (toolCall.arguments) {
      // biome-ignore lint/performance/useTopLevelRegex: <explanation>
      const match = toolCall.arguments.match(/\(([\s\S]*)\)/);
      if (match?.[1]) {
        functionArgs = match[1].trim();
      } else {
        functionArgs = toolCall.arguments;
      }
    }

    return (
      <div className="my-2 border border-muted/50 dark:border-muted/20 rounded p-2 text-sm bg-muted/20 dark:bg-muted/10">
        <div
          className="flex items-center justify-between cursor-pointer select-none"
          onClick={handleToggle}
          onKeyDown={(e) => e.key === "Enter" && handleToggle()}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: <explanation>
          tabIndex={0}
          aria-expanded={!!toolCall.isOutputVisible} // Accessibility
        >
          <span className="flex items-center gap-2 font-mono text-xs">
            {/* Using settings icon instead */}
            <Icon
              icon="settings"
              size="sm"
              className="text-primaryaccent flex-shrink-0"
            />
            Tool Call: {toolCall.type}
            {functionArgs && (
              <span className="text-muted-foreground ml-1">{functionArgs}</span>
            )}
            {/* Show loading spinner when tool is being called */}
            {isLoading && (
              <Icon
                icon="dots-loading"
                className="w-4 h-4 fill-primaryaccent animate-pulse"
              />
            )}
          </span>
          {/* Show toggle only if there is output to show/hide */}
          {toolCall.output != null &&
            (toolCall.isOutputVisible ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            ))}
        </div>
        {/* Use details/summary or conditional rendering for output */}
        {toolCall.isOutputVisible && toolCall.output != null && (
          <pre
            className="mt-2 p-2 bg-background rounded text-xs overflow-auto max-h-60 border border-border/40
          scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent hover:scrollbar-thumb-muted-foreground/40
          dark:scrollbar-thumb-muted-foreground/10 dark:bg-background dark:border-border/20 dark:text-foreground/90"
          >
            {toolCall.output || ""} {/* Ensure output is not undefined */}
          </pre>
        )}

        {/* Show loading message when output is not yet available */}
        {toolCall.isOutputVisible && isLoading && (
          <div className="mt-2 p-2 flex items-center gap-2 text-xs text-muted-foreground/70 dark:text-muted-foreground/50 bg-background/40 rounded border border-border/20 dark:border-border/10">
            <Icon
              icon="dots-loading"
              className="w-5 h-5 fill-primaryaccent/80"
            />
            <span>Processing tool call...</span>
          </div>
        )}
      </div>
    );
  },
);
ToolCallBlock.displayName = "ToolCallBlock";

const ResponseBlock = memo(
  ({
    response,
    reasoning,
    tool_calls,
    onToggleToolVisibility,
  }: ResponseBlockProps) => {
    // Keep existing thinking extraction logic for <thinking> tags
    const [thinkingText, outsideText] = splitThinkingContent(response);
    // Add state to control visibility (off by default)
    const [isThinkingVisible, setIsThinkingVisible] = useState(false);
    const [isReasoningVisible, setIsReasoningVisible] = useState(false);
    const [isThinkingDone, setIsThinkingDone] = useState(false); // State to track if thinking is done
    const [thinkingStartTime, setThinkingStartTime] = useState<number | null>(
      null,
    ); // State to track the start time of thinking
    const [thinkingDuration, setThinkingDuration] = useState<number>(0); // State to track the duration of thinking

    // Reference to the reasoning container for auto-scrolling
    const reasoningRef = useRef<HTMLDivElement>(null);

    // Filter tool and output blocks from reasoning text
    const filteredReasoning = reasoning
      ? filterToolOutputBlocks(reasoning)
      : "";

    // Auto-scroll reasoning content with adaptive speed based on distance
    useEffect(() => {
      if (!(filteredReasoning && reasoningRef.current)) {
        return;
      }

      let animationId: number;
      const container = reasoningRef.current;

      // Start at current scroll position
      let currentScrollPosition = container.scrollTop;

      // Animation function for smooth scrolling at 60fps with adaptive speed
      const scrollStep = () => {
        if (!container) {
          return;
        }

        // Get the maximum scroll position (bottom)
        const maxScroll = container.scrollHeight - container.clientHeight;

        // Calculate how far we are from the bottom
        const distanceToBottom = maxScroll - currentScrollPosition;

        // If we're not at the bottom yet
        if (distanceToBottom > 0.5) {
          // Small threshold to avoid floating point issues
          // Scale the scroll speed based on distance
          // Minimum speed: 0.2px per frame
          // Maximum speed: proportional to distance (5% of remaining distance)
          const scrollSpeed = Math.max(0.2, distanceToBottom * 0.05);

          // Move down by the calculated amount
          currentScrollPosition += scrollSpeed;

          // Don't overshoot the bottom
          currentScrollPosition = Math.min(currentScrollPosition, maxScroll);

          // Apply the new scroll position
          container.scrollTop = currentScrollPosition;
        }

        // Continue animation regardless - to catch new content
        animationId = requestAnimationFrame(scrollStep);
      };

      // Start the animation
      animationId = requestAnimationFrame(scrollStep);

      // Clean up animation on unmount
      return () => {
        cancelAnimationFrame(animationId);
      };
    }, [filteredReasoning]);

    // Update isThinkingDone based on the presence of </think> in the response
    useEffect(() => {
      if (response.includes("<think>") && thinkingStartTime === null) {
        setThinkingStartTime(Date.now());
      }

      if (response.includes("</think>") && !isThinkingDone) {
        const endTime = Date.now();
        const duration = endTime - (thinkingStartTime ?? 0);
        setThinkingDuration(duration);
        setIsThinkingDone(true);
      }
    }, [response, thinkingStartTime, isThinkingDone]);

    const hasReasoningOrTools =
      filteredReasoning || (tool_calls && tool_calls.length > 0);
    const hasThinkingTagContent = !!thinkingText;

    const { snippets } = useExecutedToolsSnippets(tool_calls || []);

    return (
      <>
        {/* Container for Reasoning, Tools, and <thinking> text */}
        {(hasReasoningOrTools || hasThinkingTagContent) && (
          <div
            className={cn(
              "border-l border-mutedaccent text-xs pl-4 pr-2 my-4", // Adjusted padding
              !outsideText && "mb-0", // Reduce bottom margin if no main text follows
            )}
          >
            {/* Reasoning section */}
            {filteredReasoning && (
              <div className="my-2">
                <div
                  className="flex items-center justify-between cursor-pointer select-none"
                  onClick={() => setIsReasoningVisible(!isReasoningVisible)}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    setIsReasoningVisible(!isReasoningVisible)
                  }
                  // biome-ignore lint/a11y/useSemanticElements: <explanation>
                  role="button"
                  tabIndex={0}
                  aria-expanded={isReasoningVisible}
                >
                  <span className="flex items-center gap-2 font-mono text-xs">
                    <Icon icon="brain" size="sm" className="flex-shrink-0" />
                    Reasoning
                  </span>
                  {isReasoningVisible ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </div>

                {/* Reasoning content - always visible but height varies based on toggle state */}
                <div
                  ref={reasoningRef}
                  className={cn(
                    "whitespace-pre-wrap mt-2 opacity-80 bg-background/50 rounded border border-border/30 overflow-y-auto overflow-x-hidden",
                    "p-2 scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent hover:scrollbar-thumb-muted-foreground/40",
                    "transition-all duration-300 ease-in-out dark:scrollbar-thumb-muted-foreground/10 dark:border-border/20", // Smooth transition for height changes
                    isReasoningVisible ? "max-h-96" : "max-h-28", // Taller when expanded, moderate preview when collapsed
                  )}
                >
                  {filteredReasoning}
                </div>
              </div>
            )}

            {/* Render tool calls */}
            {tool_calls?.map((tc) => (
              <ToolCallBlock
                key={`${tc.index}-${tc.type}`}
                toolCall={tc}
                onToggleVisibility={onToggleToolVisibility}
              />
            ))}

            {/* Thinking text toggle button */}
            {thinkingText && (
              <div className="my-2">
                <div
                  className="flex items-center justify-between cursor-pointer select-none"
                  onClick={() => setIsThinkingVisible(!isThinkingVisible)}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    setIsThinkingVisible(!isThinkingVisible)
                  }
                  // biome-ignore lint/a11y/useSemanticElements: <explanation>
                  role="button"
                  tabIndex={0}
                  aria-expanded={isThinkingVisible}
                >
                  <span className="flex items-center gap-2 font-mono text-xs">
                    {isThinkingDone ? (
                      <>
                        <Icon
                          icon={"brain"}
                          size="sm"
                          className="text-primaryaccent flex-shrink-0"
                        />
                        Thinking Done ({formatDuration(thinkingDuration)})
                      </>
                    ) : (
                      <>
                        <Icon
                          icon={"progress-activity"}
                          size="sm"
                          className="text-primaryaccent flex-shrink-0"
                        />
                        Thinking
                      </>
                    )}
                  </span>
                  {isThinkingVisible ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </div>

                {/* Conditionally render thinking content */}
                {isThinkingVisible && (
                  <div
                    className="whitespace-pre-wrap mt-2 italic opacity-60 
                  p-2 bg-background/50 rounded border border-border/30 dark:border-border/20
                  scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent hover:scrollbar-thumb-muted-foreground/40
                  dark:scrollbar-thumb-muted-foreground/10 overflow-auto max-h-96"
                  >
                    {thinkingText}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {/* Render website links */}
        <div
          className={cn(
            "bg-secondary rounded-md p-3 flex gap-3 overflow-x-auto text-sm my-3",
            snippets.length === 0 && "hidden",
          )}
        >
          {snippets.map(({ title, url, domain }) => (
            <a
              key={url}
              className="block w-[200px] h-[70px] overflow-hidden shrink-0 bg-background rounded-md p-2 hover:bg-primary hover:text-primary-foreground"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                src={`https://www.google.com/s2/favicons?domain=${domain}&size=16`}
                alt={title ?? ""}
                width={16}
                height={16}
                className="rounded-full inline-block mr-2 text-ellipsis"
              />
              {title}
            </a>
          ))}
        </div>
        {/* Render the main response content (outside thinking tags) */}
        {outsideText && <EnrichedBlock text={outsideText} />}
      </>
    );
  },
);

ResponseBlock.displayName = "ResponseBlock";

// Helper function to format duration in seconds with three decimal points
function formatDuration(duration: number): string {
  const seconds = duration / 1000;
  return `${seconds.toFixed(3)}s`;
}

export default ResponseBlock;
